import * as React from 'react';
import clsx from 'clsx';
import { colors } from '../foundation';
import { sliderClasses, browserClasses } from './twClasses';

type SliderAttributes = React.InputHTMLAttributes<HTMLInputElement>;

interface Props extends Omit<SliderAttributes, 'onChange' | 'value'> {
  min?: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
  disabled?: boolean;
  minGap?: number;
  onChange: (min: number, max?: number) => void;
}

const PercentageRangeSlider = ({
  min = 0,
  max,
  minLabel,
  maxLabel,
  disabled = false,
  minGap = 5,
  onChange,
  ...rest
}: Props) => {
  const minPercentage = min;
  const maxPercentage = max;

  const handleMinChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newPreProgress = parseInt(e.target.value, 10);
      if (newPreProgress <= max - minGap) {
        onChange(max, newPreProgress);
      }
    },
    [onChange, max, minGap]
  );

  const handleMaxChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newProgress = parseInt(e.target.value, 10);
      if (newProgress >= min + minGap) {
        onChange(newProgress, min);
      }
    },
    [onChange, min, minGap]
  );

  return (
    <div className="relative w-full">
      {/* Background track */}
      <div
        className="absolute h-4 w-full rounded-2"
        style={{
          background: `linear-gradient(to right, ${colors.red[25]} 0%, ${
            colors.red[25]
          } ${minPercentage}%, #00a892 ${minPercentage}%, #00a892 ${maxPercentage}%, ${
            colors.red[25]
          } ${maxPercentage}%, ${colors.red[25]} 100%)`,
        }}
      />

      <input
        {...rest}
        type="range"
        min={0}
        max={100}
        value={min}
        disabled={disabled}
        onChange={handleMinChange}
        className={clsx(
          sliderClasses,
          browserClasses,
          'pointer-events-none absolute top-0 bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:pointer-events-auto'
        )}
      />

      <input
        {...rest}
        type="range"
        min={0}
        max={100}
        value={max}
        disabled={disabled}
        onChange={handleMaxChange}
        className={clsx(
          sliderClasses,
          browserClasses,
          'pointer-events-none absolute top-0 bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:pointer-events-auto'
        )}
      />

      {(minLabel || maxLabel) && (
        <div className="mt-12 flex w-full justify-between bg-transparent">
          <span
            className="flex min-h-fit min-w-fit justify-end whitespace-nowrap text-sm"
            style={{ width: `${minPercentage}%` }}
          >
            {minLabel}
          </span>
          <span
            className="flex min-h-fit min-w-fit justify-start whitespace-nowrap  text-sm"
            style={{ width: `${100 - maxPercentage}%` }}
          >
            {maxLabel}
          </span>
        </div>
      )}
    </div>
  );
};

export default PercentageRangeSlider;
